/**
 * RCC Surface #10 — No-POD release workflow.
 *
 * For individual accounts with no POD beneficiary: record estate documentation
 * receipt, generate Claim Against Estate + Release and Hold Harmless forms,
 * release funds.
 *
 * Wave 2 (locked 2026-04-27 decision #2): MO 1-year + 30-day statutory hold
 * per RSMo 473.020.2. Date of death is captured at the start; release is
 * gated until `dod + 395 days` based on `deceased.moHoldMode` SystemSetting:
 *   - 'warn'  (default) : soft warning shown, supervisor may override
 *   - 'block'           : hard-block until cutoff date
 */

import { useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import { Button, Pill } from '@/components/primitives';
import { SurfaceHeader } from '../components/SurfaceHeader';

type Step = 'docs' | 'forms' | 'release' | 'done';

export function NoPodRelease(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const { config } = useConfig();
  const [step, setStep] = useState<Step>('docs');
  const [dateOfDeath, setDateOfDeath] = useState<string>('');
  const [overrideMoHold, setOverrideMoHold] = useState<boolean>(false);
  const [docsReceived, setDocsReceived] = useState<{ death: boolean; letters: boolean; affidavit: boolean }>({
    death: false,
    letters: false,
    affidavit: false,
  });
  const [formsIssued, setFormsIssued] = useState<{ claim: boolean; release: boolean }>({
    claim: false,
    release: false,
  });

  const moHoldMode = (config?.systemSettings?.['deceased.moHoldMode'] as string | undefined) ?? 'warn';

  const moHold = useMemo(() => {
    if (!dateOfDeath) return null;
    const dod = new Date(dateOfDeath);
    if (Number.isNaN(dod.getTime())) return null;
    const cutoff = new Date(dod);
    cutoff.setDate(cutoff.getDate() + 395);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysRemaining = Math.ceil((cutoff.getTime() - today.getTime()) / 86_400_000);
    return {
      cutoffIso: cutoff.toISOString().slice(0, 10),
      daysRemaining,
      active: daysRemaining > 0,
    };
  }, [dateOfDeath]);

  const moHoldBlocks = !!moHold?.active && moHoldMode === 'block' && !overrideMoHold;
  const moHoldWarns = !!moHold?.active && moHoldMode === 'warn';

  const allDocsReceived = !!dateOfDeath && docsReceived.death && docsReceived.letters && docsReceived.affidavit;
  const allFormsIssued = formsIssued.claim && formsIssued.release;

  const release = (): void => {
    void apiClient.retail.events
      .write({
        eventType: 'no-pod-release-completed',
        eventCategory: 'compliance',
        severity: 'warn',
        actor: user?.upn ?? 'unknown',
        payloadJson: JSON.stringify({
          dateOfDeath,
          moHoldCutoff: moHold?.cutoffIso ?? null,
          moHoldMode,
          moHoldOverride: overrideMoHold,
          docsReceived,
          formsIssued,
        }),
      })
      .catch(() => undefined);
    setStep('done');
  };

  return (
    <div>
      <SurfaceHeader
        title="No-POD Release"
        scope="Release funds from an individual account with no POD beneficiary. Requires estate documentation + signed Release and Hold Harmless."
        surfaceNumber={10}
        role="FSR · Supervisor approval"
      />

      <div style={{ display: 'grid', gap: 'var(--sp-6)', maxWidth: 640 }}>
        <StepHeader active={step === 'docs'} done={step !== 'docs'} num={1} label="Estate documents received" />
        {step === 'docs' && (
          <div style={panelStyle}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', marginBottom: 'var(--sp-4)' }}>
              <span style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-500)' }}>Date of death</span>
              <input
                type="date"
                value={dateOfDeath}
                onChange={(e) => setDateOfDeath(e.target.value)}
                style={{
                  padding: 'var(--sp-3)',
                  border: '1px solid var(--ink-300)',
                  borderRadius: 'var(--r-md)',
                  fontSize: 13,
                  width: 220,
                }}
              />
            </label>
            {moHold && (
              <div
                style={{
                  padding: 'var(--sp-3)',
                  background: moHoldBlocks ? 'var(--err-bg)' : moHoldWarns ? 'var(--warn-bg, #fff8e1)' : 'var(--ok-bg, #e8f5e9)',
                  color: moHoldBlocks ? 'var(--err-text)' : moHoldWarns ? 'var(--warn-text, #7b5800)' : 'var(--ok-text, #1b5e20)',
                  borderRadius: 'var(--r-md)',
                  fontSize: 12,
                  marginBottom: 'var(--sp-4)',
                }}
              >
                {moHold.active ? (
                  <>
                    <strong>MO 1-year + 30-day hold active</strong> (RSMo 473.020.2): cutoff {moHold.cutoffIso}
                    {' '}— {moHold.daysRemaining} day{moHold.daysRemaining === 1 ? '' : 's'} remaining.
                    {' '}Mode: <Pill variant={moHoldBlocks ? 'err' : 'warn'}>{moHoldMode}</Pill>
                    {moHoldMode === 'warn' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
                        <input type="checkbox" checked={overrideMoHold} onChange={(e) => setOverrideMoHold(e.target.checked)} />
                        Supervisor override (document reason in audit notes)
                      </label>
                    )}
                  </>
                ) : (
                  <>MO statutory hold cleared on {moHold.cutoffIso}. Release allowed.</>
                )}
              </div>
            )}
            <Check
              label="Certified death certificate"
              checked={docsReceived.death}
              onChange={(v) => setDocsReceived((s) => ({ ...s, death: v }))}
            />
            <Check
              label="Letters Testamentary / Letters of Administration"
              checked={docsReceived.letters}
              onChange={(v) => setDocsReceived((s) => ({ ...s, letters: v }))}
            />
            <Check
              label="Small estate affidavit (if applicable)"
              checked={docsReceived.affidavit}
              onChange={(v) => setDocsReceived((s) => ({ ...s, affidavit: v }))}
            />
            <Button
              variant="primary"
              onClick={() => setStep('forms')}
              disabled={!allDocsReceived || moHoldBlocks}
              style={{ marginTop: 'var(--sp-5)' }}
            >
              {moHoldBlocks ? 'Blocked by MO 1yr+30day hold' : 'Proceed to forms'}
            </Button>
          </div>
        )}

        <StepHeader
          active={step === 'forms'}
          done={step === 'release' || step === 'done'}
          num={2}
          label="Generate forms"
        />
        {step === 'forms' && (
          <div style={panelStyle}>
            <Check
              label="Claim Against Estate form issued"
              checked={formsIssued.claim}
              onChange={(v) => setFormsIssued((s) => ({ ...s, claim: v }))}
            />
            <Check
              label="Release and Hold Harmless signed"
              checked={formsIssued.release}
              onChange={(v) => setFormsIssued((s) => ({ ...s, release: v }))}
            />
            <Button
              variant="primary"
              onClick={() => setStep('release')}
              disabled={!allFormsIssued}
              style={{ marginTop: 'var(--sp-5)' }}
            >
              Proceed to release
            </Button>
          </div>
        )}

        <StepHeader active={step === 'release'} done={step === 'done'} num={3} label="Release funds" />
        {step === 'release' && (
          <div style={panelStyle}>
            <p style={{ fontSize: 13, color: 'var(--ink-700)' }}>
              Confirm funds release. Writes SessionEvent <code>no-pod-release-completed</code>.
            </p>
            <Button variant="green" onClick={release}>
              Confirm release
            </Button>
          </div>
        )}

        {step === 'done' && (
          <div
            style={{
              padding: 'var(--sp-6)',
              background: 'var(--ok-bg)',
              color: 'var(--ok-text)',
              borderRadius: 'var(--r-md)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-4)',
            }}
          >
            <Pill variant="ok">Complete</Pill>
            No-POD release recorded for audit.
          </div>
        )}
      </div>
    </div>
  );
}

function StepHeader({
  num,
  label,
  active,
  done,
}: {
  num: number;
  label: string;
  active: boolean;
  done: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4) 0',
        borderBottom: '1px solid var(--surface-border)',
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 'var(--r-full)',
          background: done ? 'var(--ok-fill)' : active ? 'var(--brand-fill)' : 'var(--ink-300)',
          color: done ? 'var(--on-status-fill)' : active ? 'var(--on-brand)' : 'var(--surface-card)',
          display: 'grid',
          placeItems: 'center',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {done ? '✓' : num}
      </span>
      <span
        style={{
          fontSize: 14,
          fontWeight: active || done ? 600 : 400,
          color: active ? 'var(--pcb-blue-dark)' : done ? 'var(--pcb-green)' : 'var(--ink-600)',
        }}
      >
        {label}
      </span>
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', padding: 'var(--sp-3) 0', fontSize: 13 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

const panelStyle: React.CSSProperties = {
  background: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: 'var(--r-md)',
  padding: 'var(--sp-6)',
};
